import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  deleteDoc,
  doc,
  setDoc,
  getDocs,
  collection,
  addDoc,
  onSnapshot,
  runTransaction,
  query,
  where,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { memo, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Feed, Form, Icon, Input } from 'semantic-ui-react';
import * as selectors from '../../redux/selectors';
import * as actions from '../../redux/actions';
import moment from 'moment';
import EmojiPicker from 'emoji-picker-react';

const firebaseConfig = {
  apiKey: 'AIzaSyB5_wVxmSwgQaudfgfZASbIN3NsV_DC2DY',
  authDomain: 'chat-eb7db.firebaseapp.com',
  projectId: 'chat-eb7db',
  storageBucket: 'chat-eb7db.appspot.com',
  messagingSenderId: '95565963179',
  appId: '1:95565963179:web:26e6c3a92806e853bb7cee',
  measurementId: 'G-TQY2CMMRZY',
};

const useFirebase = () => {
  const [db, setDb]: any = useState(null);

  useEffect(() => {
    const app = initializeApp(firebaseConfig);
    const db = getFirestore(app);
    setDb(db);
  }, []);

  return db;
};

const useStorage = () => {
  const [storage, setStorage]: any = useState(null);

  useEffect(() => {
    const app = initializeApp(firebaseConfig);
    const storage = getStorage(app);
    setStorage(storage);
  }, []);

  const [media, setMedia]: any = useState(null);
  const [percent, setPercent] = useState(0);

  const clear = () => {
    setMedia(null);
    setPercent(0);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const file = e.target?.files[0];

    if (!file) return;

    const storageRef = ref(storage, `files/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      snapshot => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setPercent(progress);
      },
      error => {
        alert(error);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((url: string) => {
          const getFileOrImageTypeFromFilename = (file: any): 'file' | 'image' => {
            const fileExtension = file.name.split('.').pop();
            if (fileExtension === 'png') return 'image';
            if (fileExtension === 'jpg') return 'image';
            if (fileExtension === 'jpeg') return 'image';
            if (fileExtension === 'gif') return 'image';
            if (fileExtension === 'svg') return 'image';
            if (fileExtension === 'bmp') return 'image';
            if (fileExtension === 'tiff') return 'image';
            if (fileExtension === 'webp') return 'image';
            if (fileExtension === 'ico') return 'image';

            return 'file';
          };

          setMedia({ url, file: file.name, type: getFileOrImageTypeFromFilename(file) });
        });
      }
    );
  };

  return { handleSubmit, media, percent, clear };
};

const useIsEditing = (user: any) => {
  const [isEditing, setIsEditing] = useState(false);
  const [lastEditTime, setLastEditTime] = useState(null);

  const db = useFirebase();

  useEffect(() => {
    const checkInterval = setInterval(() => {
      if (!lastEditTime) return setIsEditing(false);

      const currentlyEditing = moment().diff(moment(lastEditTime), 'seconds') < 10;

      setIsEditing(currentlyEditing);
    }, 1000);

    return () => clearInterval(checkInterval);
  }, [lastEditTime]);

  useEffect(() => {
    if (!db) return;

    setIsEditing(false);

    const unsubscribe = collection(db, 'editing');

    onSnapshot(unsubscribe, querySnapshot => {
      querySnapshot.forEach(doc => {
        const data = doc.data();

        if (!data.editedAt) {
          setIsEditing(false);
        }

        if (data.user !== user.name) {
          setLastEditTime(data.editedAt);
        }
      });
    });
  }, [db]);

  return isEditing;
};

const TypingIndicator = ({ user }: any) => {
  const isEditing = useIsEditing(user);

  if (!isEditing) return <></>;

  return (
    <img
      src="https://media.tenor.com/EZc7Xubv14AAAAAC/typing-texting.gif"
      height={40}
      style={{
        float: 'left',
      }}
    />
  );
};

const LoadingIndicator = ({ progress }: any) => {
  if (!progress) return <></>;

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignContent: 'end' }}>
      <i className="fa fa-circle-notch fa-2x fa-spin" style={{ float: 'left', margin: '0 0 0 10px' }} />
    </div>
  );
};

const ChatPage = ({ user }: any) => {
  const db = useFirebase();
  const [lastUpdated, setLastUpdated]: any = useState(moment());
  const [messages, setMessages]: any = useState([]);
  const [readMessages, setReadMessages]: any = useState([]);
  const editing = useIsEditing(user);
  const messagesCollection = 'messages';
  const { handleSubmit, media, percent, clear } = useStorage();

  useEffect(() => {
    addMessage(media?.url, media?.type, media?.file);
    clear();
    setLastUpdated(moment());
  }, [media]);

  useEffect(() => {
    if (!user?.email) return;

    // disable autocomplete on the input field
    const input = document.getElementById('message');
    if (input) {
      input.setAttribute('autocomplete', 'off');
    }

    const users = ['jacob@culturemap.com', 'jacobswain@gmail.com', 'lindsey@culturemap.com'];

    if (!users.includes(user.email)) {
      window.location.href = '/home';
    }
  }, [user]);

  useEffect(() => {
    if (!lastUpdated) return;

    const interval = setInterval(() => {
      if (moment().diff(moment(lastUpdated), 'minutes') > 5) {
        window.location.href = '/home';
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [lastUpdated]);

  const markAsRead = async () => {
    await runTransaction(db, async transaction => {
      // get all messages
      // find messages where readAt is null and user is not user.name
      // update readAt to now
      const messagesRef = collection(db, messagesCollection);

      const querySnapshot = await query(messagesRef, where('user', '!=', user.name));
      const docs = await getDocs(querySnapshot);

      docs.forEach(doc => {
        const data = doc.data();

        if (!data.readAt && data.user !== user.name) {
          console.log('updating read status');
          transaction.update(doc.ref, { readAt: moment().format() });
        }
      });
    });
  };

  useEffect(() => {
    if (!db) return;

    markAsRead();
    setLastSeenStatus();
  }, [db]);

  const setLastSeenStatus = async () => {
    await addDoc(collection(db, 'history'), {
      user: user.name,
      createdAt: moment().format(),
    });
  };

  const setEditingStatus = async (editing: boolean) => {
    if (!db) return;
    const docRef = doc(db, 'editing', user.id);
    await setDoc(docRef, {
      user: user.name,
      editedAt: editing ? moment().format() : null,
    });
  };

  const addMessage = async (message: string, type: 'text' | 'file' | 'image', file?: string) => {
    if (!message?.trim()) return;

    await addDoc(collection(db, messagesCollection), {
      message: message,
      user: user.name,
      type,
      createdAt: moment().format('YYYY-MM-DD HH:mm:ss'),
      file: file || '',
    });
  };

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!scrolled && messages.length > 0) {
      document.querySelector('.chat-feed')?.scrollTo(0, document.querySelector('.chat-feed')?.scrollHeight);

      setTimeout(() => {
        document.querySelector('.chat-feed')?.scrollTo(0, document.querySelector('.chat-feed')?.scrollHeight);
      }, 1000);

      setTimeout(() => {
        document.querySelector('.chat-feed')?.scrollTo(0, document.querySelector('.chat-feed')?.scrollHeight);
      }, 2000);

      setScrolled(true);
    }
  }, [messages, scrolled]);

  useEffect(() => {
    if (!db) return;
    const unsubscribe = collection(db, messagesCollection);

    onSnapshot(unsubscribe, querySnapshot => {
      const newMesssages: any = [];

      querySnapshot.forEach(async doc => {
        newMesssages.push(doc);
      });

      newMesssages.sort((a: any, b: any) => {
        return moment(a.data().createdAt).diff(moment(b.data().createdAt));
      });

      setMessages([...newMesssages]);
    });
  }, [db]);

  const regex = new RegExp(
    '^(http[s]?:\\/\\/(www\\.)?|ftp:\\/\\/(www\\.)?|www\\.){1}([0-9A-Za-z-\\.@:%_+~#=]+)+((\\.[a-zA-Z]{2,3})+)(/(.)*)?(\\?(.)*)?'
  );

  const MessageWrapper = ({ record, idx }: any) => {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [hovering, setHovering] = useState(false);
    return (
      <div onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)} key={idx}>
        <Feed.Event key={idx}>
          <Feed.Content
            style={{
              borderTop: '1px solid gainsboro',
              color: 'white!important',
              paddingTop: 10,
              maxWith: '20vw!important',
              display: 'flex',
              justifyContent: record.data().user === user.name ? 'end' : 'start',
            }}
          >
            <div
              style={{
                margin: 10,
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '90vw!important',
                justifyContent: record.data().user === user.name ? 'end' : 'start',
              }}
            >
              <Message record={record} />

              {hovering ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '1rem',
                    marginTop: '1rem',
                  }}
                >
                  <Icon
                    name="trash alternate outline"
                    color="red"
                    onClick={async () => {
                      await deleteDoc(doc(db, messagesCollection, record.id));
                    }}
                  />
                  <Icon name="smile outline" onClick={() => setShowEmojiPicker(!showEmojiPicker)} />
                  <div style={{ display: showEmojiPicker ? 'unset' : 'none' }}>
                    <EmojiPicker
                      onEmojiClick={(e: any) => {
                        setDoc(record.ref, {
                          ...record.data(),
                          emoji: e.emoji,
                        }).then(() => console.log('updated'));
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '1rem',
                    marginTop: '1rem',
                    height: 15,
                  }}
                />
              )}
            </div>
          </Feed.Content>
        </Feed.Event>
      </div>
    );
  };

  // memoize the message wrapper so it only re-render when hovering changes
  const MemoizedMessageWrapper = memo(MessageWrapper, (prevProps, nextProps) => {
    return prevProps.hovering === nextProps.hovering;
  });

  const Message = ({ record }: any): JSX.Element => {
    const message = record.data();

    switch (message.type) {
      case 'image':
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              maxWidth: '90vw!important',
              justifyContent: message.user === user.name ? 'end' : 'start',
            }}
          >
            <img
              style={{
                borderRadius: '1rem',
                maxWidth: '50vw',
                maxHeight: '50vh',
                objectFit: 'cover',
              }}
              src={message.message}
              onClick={() => window.open(message.message, '_blank')}
            />
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'end', marginBottom: '.5rem' }}>
              <div style={{ fontSize: '.8rem' }}>{moment(message.createdAt, 'YYYY-MM-DD HH:mm:ss').format('LLL')}</div>
              {message.emoji && (
                <div
                  style={{ fontSize: 22 }}
                  onClick={() => {
                    setDoc(record.ref, {
                      ...message,
                      emoji: null,
                    }).then(() => console.log('updated'));
                  }}
                >
                  {message.emoji}{' '}
                </div>
              )}
            </div>
          </div>
        );

      case 'file':
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              maxWidth: '90vw!important',
              justifyContent: message.user === user.name ? 'end' : 'start',
            }}
          >
            <a href={message.message} target="_blank">
              {message.file}
            </a>
            <div style={{ fontSize: '.8rem' }}>{moment(message.createdAt, 'YYYY-MM-DD HH:mm:ss').format('LLL')}</div>
            {message.emoji && (
              <div
                onClick={() => {
                  setDoc(record.ref, {
                    ...message,
                    emoji: null,
                  }).then(() => console.log('updated'));
                }}
                style={{ fontSize: 22 }}
              >
                {message.emoji}{' '}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div
            style={{
              borderRadius: '1rem',
              backgroundColor: message.user === user.name ? '#1982FC' : 'grey',
              padding: 5,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              margin: 5,
              maxWidth: '85vw',
              paddingLeft: 10,
              paddingRight: 10,
              color: 'white',
              textDecoration: regex.test(message.message) ? 'underline' : 'none',
              cursor: regex.test(message.message) ? 'pointer' : 'default',
            }}
            onClick={() => {
              const content = message.message;

              if (regex.test(content)) {
                if (!content?.includes('http')) {
                  window.open(`http://${content}`, '_blank');
                  return;
                }
                window.open(content, '_blank');
              }
            }}
          >
            <div style={{ display: 'flex', gap: 5 }}>
              {message.readAt && <Icon name="check circle" />}
              <div style={{ fontWeight: 'bold', marginRight: '1rem' }}>{message.message}</div>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'end',
                position: 'relative',
                marginBottom: '.5rem',
              }}
            >
              <div style={{ fontSize: '.8rem', paddingRight: 5, minWidth: 60 }}>
                {moment(message.createdAt, 'YYYY-MM-DD HH:mm:ss').format('LLL')}
              </div>
              {message.emoji && (
                <div
                  onClick={() => {
                    setDoc(record.ref, {
                      ...message,
                      emoji: null,
                    }).then(() => console.log('updated'));
                  }}
                  style={{ fontSize: 22, position: 'absolute', right: 2, marginTop: 20 }}
                >
                  {message.emoji}{' '}
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  const fileInputRef = useRef();

  return (
    <div className="chat-page">
      <div className="chat-feed">
        <Feed>
          <>
            {messages.map((message: any, idx: any) => {
              return (
                <div key={idx}>
                  <MemoizedMessageWrapper record={message} idx={idx} />
                </div>
              );
            })}

            <TypingIndicator user={user} editing={editing} />
          </>
        </Feed>
        <LoadingIndicator progress={percent} />
      </div>

      <div className="chat-controls">
        <Form
          id="chat-form"
          onSubmit={async e => {
            try {
              const form = new FormData(e.target);
              const message: any = form.get('message');
              addMessage(message, 'text');
              document.getElementById('chat-form')?.reset();
            } catch (error) {
              console.log(error);
            }
          }}
        >
          <Input
            id="message"
            style={{ flexGrow: 1 }}
            name="message"
            autoComplete="off"
            placeholder="Type your message here..."
            onChange={async (e, { value }) => {
              // set editing status for user

              if (value?.trim()?.length > 0) {
                await setEditingStatus(true);
              } else {
                await setEditingStatus(false);
              }
            }}
          />
        </Form>

        <>
          <Button
            basic
            icon="upload"
            primary
            onClick={() => {
              fileInputRef?.current?.click();
            }}
          />
          <input onChange={(e: any) => handleSubmit(e, user)} multiple={false} ref={fileInputRef} type="file" hidden />
        </>

        <Button
          icon="trash"
          basic
          negative
          onClick={async () => {
            const querySnapshot = await getDocs(collection(db, messagesCollection));
            querySnapshot.forEach(async (message: any) => {
              await deleteDoc(doc(db, messagesCollection, message.id));
            });
          }}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  invoices: selectors.getAllInvoices(state),
  user: selectors.getUser(state),
  sites: selectors.admin.siteOptionsDetail(state),
  users: selectors.getUserOptions(state),
  editors: selectors.getEditors(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  addInvoice: bindActionCreators(actions.invoices.addInvoice, dispatch),
  getSites: bindActionCreators(actions.admin.getSites, dispatch),
  getUsers: bindActionCreators(actions.admin.getUsers, dispatch),
  getInvoices: bindActionCreators(actions.invoices.getInvoices, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChatPage);
